import { useEffect, useMemo } from "react"
import { useQuery } from "urql"

import type { LeadTimeUnits, Product, ProductShippingQueryVariables } from "@/graphql/codegen/graphql"
import { ProductShippingDocument } from "@/graphql/codegen/graphql"
import { NotificationType } from "@/schemas/notification"
import { useNotificationsStore } from "@/stores/useNotificationsStore"

interface UseShippingProps {
  productId: number
  version?: number
}

type ProductShipping = Pick<Product, "weightPerTruckload" | "leadTimeInDays" | "minOrderQuantity" | "versionStatus"> & {
  leadTimeDisplayUnits?: LeadTimeUnits
}

interface UseShippingResponse {
  data: ProductShipping | null
  fetching: boolean
  fetch: (variables: ProductShippingQueryVariables) => void
}

export const useProductShipping = ({ productId, version }: UseShippingProps): UseShippingResponse => {
  const [{ data, error, fetching }, fetch] = useQuery({
    query: ProductShippingDocument,
    variables: {
      productId,
      version,
    },
  })

  const hasError = error?.message || data?.product.get.__typename === "ProductGetFailure"
  const notify = useNotificationsStore(({ enqueueNotification }) => enqueueNotification)

  useEffect(() => {
    if (hasError) {
      notify({
        message: "Error fetching product shipping details",
        type: NotificationType.Error,
        silent: false,
      })
    }
  }, [hasError, notify])

  const conditions = useMemo(() => {
    if (data?.product.get.__typename === "ProductGetSuccess") {
      return data.product.get.product
    }
    return null
  }, [data])

  return {
    data: conditions as ProductShipping | null,
    fetching,
    fetch,
  }
}
